(function () {
  const MINIMUM_CHARGE = 48;
  const DEFAULT_RUG = {
    pricePerSqFt: 2.5,
    maxWidthInches: 158,
    maxLengthFeet: 99,
    minOrderSqFt: 6,
    edgeFinishIncluded: true,
    edgeFinishCostPerFt: 0,
    shortLengthSurcharge: 0,
    shortLengthThresholdFt: 0
  };
  const DEFAULT_RUG_PAD = {
    pricePerSqFt: 1.5,
    maxWidthInches: 144,
    maxLengthFeet: 99,
    minWidthInches: 24,
    minLengthInches: 36
  };
  const DEFAULT_RUNNER_PAD = {
    widthsFt: [2, 3],
    floorPriceByWidth: { 2: 49.99, 3: 59.99 },
    perFtRateByWidth: { 2: 8.99, 3: 12.99 },
    floorThresholdFt: 6,
    minLengthFt: 1,
    maxLengthFt: 50
  };
  const API_FALLBACK = 'https://wellwoven-headless.vercel.app';
  let bypassCheckoutInterceptor = false;

  function toNumber(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function toInt(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function parseMaybeJson(value, fallback) {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || value.trim() === '') return fallback;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return value.split(',').map((v) => parseInt(v.trim(), 10)).filter(Boolean);
    }
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number.isFinite(amount) ? amount : 0);
  }

  function escapeAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function digitsOnly(value) {
    return String(value == null ? '' : value).replace(/[^\d]/g, '');
  }

  function normalizeNumberInput(input, preserveBlank) {
    const cleaned = digitsOnly(input.value);
    if (cleaned === '') return '';

    const parsed = parseInt(cleaned, 10);
    if (!Number.isFinite(parsed)) return '';

    const min = parseInt(input.getAttribute('data-min') || '', 10);
    const max = parseInt(input.getAttribute('data-max') || '', 10);
    let next = parsed;

    if (!preserveBlank && Number.isFinite(min)) next = Math.max(min, next);
    if (Number.isFinite(max)) next = Math.min(max, next);

    return String(next);
  }

  function moneyFromCents(cents) {
    return formatMoney((Number(cents) || 0) / 100);
  }

  function readConfig(root) {
    const id = root.getAttribute('data-config-id');
    const el = id ? document.getElementById(id) : null;
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('WW custom configurator config parse failed', err);
      return null;
    }
  }

  function getVariants(config) {
    return Array.isArray(config.product && config.product.variants)
      ? config.product.variants
      : [];
  }

  function numericVariantId(variantOrId) {
    const raw = typeof variantOrId === 'object' && variantOrId
      ? variantOrId.id
      : variantOrId;
    const str = String(raw || '');
    const match = str.match(/(\d+)$/);
    return match ? match[1] : str;
  }

  function firstAvailableVariant(config) {
    const variants = getVariants(config);
    return variants.find((v) => v.available) || variants[0] || null;
  }

  function optionNames(config) {
    const opts = config.product && config.product.options;
    if (!Array.isArray(opts)) return [];
    return opts.map((opt) => typeof opt === 'string' ? opt : opt.name);
  }

  function variantOption(variant, index) {
    return variant && variant['option' + (index + 1)] ? String(variant['option' + (index + 1)]) : '';
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function parseWidthInches(label) {
    const text = String(label || '').toLowerCase();
    const n = parseFloat(text);
    if (!Number.isFinite(n)) return 0;
    if (text.includes('inch') || text.includes('in.')) return Math.round(n);
    return Math.round(n * 12);
  }

  function formatDimension(feet, inches) {
    if (!inches) return feet + "'";
    return feet + "'" + inches + '"';
  }

  function formatArea(area) {
    return area.toFixed(1) + ' sq ft';
  }

  function customRugConfig(config) {
    const m = config.metafields || {};
    const price = toNumber(m.priceOverrideSqFt, toNumber(m.pricePerSqFt, DEFAULT_RUG.pricePerSqFt));
    return {
      pricePerSqFt: price,
      maxWidthInches: toInt(m.maxWidthInches, DEFAULT_RUG.maxWidthInches),
      maxLengthFeet: toInt(m.maxLengthFeet, DEFAULT_RUG.maxLengthFeet),
      minOrderSqFt: toNumber(m.minOrderSqFt, DEFAULT_RUG.minOrderSqFt),
      remainingLengthFt: m.remainingLengthFt ? parseFloat(m.remainingLengthFt) : null,
      remnantDiscountPct: toNumber(m.remnantDiscountPct, 0),
      edgeFinishIncluded: m.edgeFinishIncluded === undefined || m.edgeFinishIncluded === null || String(m.edgeFinishIncluded) === 'true',
      edgeFinishCostPerFt: toNumber(m.edgeFinishCostPerFt, DEFAULT_RUG.edgeFinishCostPerFt),
      shortLengthSurcharge: toNumber(m.shortLengthSurcharge, DEFAULT_RUG.shortLengthSurcharge),
      shortLengthThresholdFt: toNumber(m.shortLengthThresholdFt, DEFAULT_RUG.shortLengthThresholdFt)
    };
  }

  function runnerPadConfig(config) {
    const m = config.metafields || {};
    const widths = parseMaybeJson(m.padWidthsFt, DEFAULT_RUNNER_PAD.widthsFt).map((w) => parseInt(w, 10)).filter(Boolean);
    return {
      widthsFt: widths.length ? widths : DEFAULT_RUNNER_PAD.widthsFt,
      floorPriceByWidth: {
        2: toNumber(m.pad2ftFloorPrice, DEFAULT_RUNNER_PAD.floorPriceByWidth[2]),
        3: toNumber(m.pad3ftFloorPrice, DEFAULT_RUNNER_PAD.floorPriceByWidth[3])
      },
      perFtRateByWidth: {
        2: toNumber(m.pad2ftPerFtRate, DEFAULT_RUNNER_PAD.perFtRateByWidth[2]),
        3: toNumber(m.pad3ftPerFtRate, DEFAULT_RUNNER_PAD.perFtRateByWidth[3])
      },
      floorThresholdFt: toInt(m.padFloorThresholdFt, DEFAULT_RUNNER_PAD.floorThresholdFt),
      minLengthFt: toInt(m.padMinLengthFt, DEFAULT_RUNNER_PAD.minLengthFt),
      maxLengthFt: toInt(m.padMaxLengthFt, DEFAULT_RUNNER_PAD.maxLengthFt),
      thicknessInches: m.padThicknessInches || '1/4"'
    };
  }

  function dimensionsFromState(state) {
    const widthFeet = toInt(state.widthFeet, 0);
    const widthInches = Math.max(0, Math.min(11, parseInt(state.widthInches || 0, 10) || 0));
    const lengthFeet = state.shape === 'round' ? 0 : toInt(state.lengthFeet, 0);
    const lengthInches = state.shape === 'round' ? 0 : Math.max(0, Math.min(11, parseInt(state.lengthInches || 0, 10) || 0));
    return { widthFeet, widthInches, lengthFeet, lengthInches };
  }

  function rugBreakdown(cfg, dims, shape) {
    const widthFt = dims.widthFeet + dims.widthInches / 12;
    const lengthFt = shape === 'round' ? 0 : dims.lengthFeet + dims.lengthInches / 12;
    const area = shape === 'round'
      ? Math.PI * Math.pow(widthFt / 2, 2)
      : widthFt * lengthFt;
    const perimeter = shape === 'round'
      ? Math.PI * widthFt
      : 2 * (widthFt + lengthFt);
    const edgeCost = cfg.edgeFinishIncluded ? 0 : perimeter * cfg.edgeFinishCostPerFt;
    const shortLengthSurcharge = shape !== 'round' &&
      cfg.shortLengthThresholdFt > 0 &&
      cfg.shortLengthSurcharge > 0 &&
      lengthFt < cfg.shortLengthThresholdFt
      ? cfg.shortLengthSurcharge
      : 0;
    const subtotal = area * cfg.pricePerSqFt + edgeCost + shortLengthSurcharge;
    const discount = cfg.remnantDiscountPct > 0 ? subtotal * (cfg.remnantDiscountPct / 100) : 0;
    return {
      areaSqFt: area,
      pricePerSqFt: cfg.pricePerSqFt,
      shortLengthSurcharge,
      total: Math.max(subtotal - discount, MINIMUM_CHARGE)
    };
  }

  function validateRug(cfg, dims, shape) {
    const errors = [];
    const widthFt = dims.widthFeet + dims.widthInches / 12;
    const lengthFt = dims.lengthFeet + dims.lengthInches / 12;
    const widthInches = dims.widthFeet * 12 + dims.widthInches;
    if (widthFt <= 0) errors.push('Add a width to see your price.');
    if (shape !== 'round' && lengthFt <= 0) errors.push('Add a length to see your price.');
    if (widthInches > cfg.maxWidthInches) {
      errors.push('Maximum width is ' + Math.floor(cfg.maxWidthInches / 12) + "'" + (cfg.maxWidthInches % 12) + '".');
    }
    if (shape !== 'round' && lengthFt > cfg.maxLengthFeet) errors.push('Maximum length is ' + cfg.maxLengthFeet + ' feet.');
    const area = shape === 'round' ? Math.PI * Math.pow(widthFt / 2, 2) : widthFt * lengthFt;
    if (area > 0 && area < cfg.minOrderSqFt) errors.push('Minimum order is ' + cfg.minOrderSqFt + ' square feet.');
    if (cfg.remainingLengthFt && shape !== 'round' && lengthFt > cfg.remainingLengthFt) {
      errors.push('Only ' + cfg.remainingLengthFt.toFixed(1) + ' ft remains on this roll.');
    }
    return errors;
  }

  function runnerWidthOptions(config) {
    const names = optionNames(config);
    const widthIndex = Math.max(0, names.findIndex((name) => /width|w\s/i.test(name || '')));
    const values = unique(getVariants(config).map((variant) => variantOption(variant, widthIndex)));
    return { widthIndex, values };
  }

  function variantForWidth(config, widthLabel) {
    const { widthIndex } = runnerWidthOptions(config);
    return getVariants(config).find((variant) =>
      variant.available && variantOption(variant, widthIndex) === widthLabel
    ) || getVariants(config).find((variant) =>
      variantOption(variant, widthIndex) === widthLabel
    ) || firstAvailableVariant(config);
  }

  function buildFields(type, config, state) {
    const runner = type === 'runner';
    const runnerPad = type === 'runner_pad';
    const roundAreaRug = type === 'area_rug' && state.shape === 'round';
    const widthOptions = runner ? runnerWidthOptions(config).values : [];
    const padCfg = runnerPad ? runnerPadConfig(config) : null;

    return [
      type === 'area_rug' ? `
        <div class="ww-custom-row">
          <span class="ww-custom-label">Shape</span>
          <div class="ww-custom-segmented">
            <button class="ww-custom-choice" type="button" data-shape="rectangle" aria-pressed="${state.shape !== 'round'}">Rectangle</button>
            <button class="ww-custom-choice" type="button" data-shape="round" aria-pressed="${state.shape === 'round'}">Round</button>
          </div>
        </div>
      ` : '',
      runner ? `
        <label>
          <span class="ww-custom-label">Width</span>
          <select class="ww-custom-select" data-field="runnerWidth">
            ${widthOptions.map((value) => `<option value="${value}" ${value === state.runnerWidth ? 'selected' : ''}>${value}</option>`).join('')}
          </select>
        </label>
      ` : '',
      runnerPad ? `
        <label>
          <span class="ww-custom-label">Width</span>
          <select class="ww-custom-select" data-field="runnerPadWidth">
            ${padCfg.widthsFt.map((value) => `<option value="${value}" ${String(value) === String(state.runnerPadWidth) ? 'selected' : ''}>${value}'</option>`).join('')}
          </select>
        </label>
      ` : '',
      !runnerPad ? `
        <div class="ww-custom-grid">
          ${runner ? '' : `
          <label>
            <span class="ww-custom-label">${roundAreaRug ? 'Diameter feet' : 'Width feet'}</span>
            <input class="ww-custom-input" data-field="widthFeet" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-min="0" value="${escapeAttr(state.widthFeet)}" placeholder="0">
          </label>
          <label>
            <span class="ww-custom-label">${roundAreaRug ? 'Diameter inches' : 'Width inches'}</span>
            <input class="ww-custom-input" data-field="widthInches" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-min="0" data-max="11" value="${escapeAttr(state.widthInches)}" placeholder="0">
          </label>
          `}
          ${roundAreaRug ? '' : `
          <label>
            <span class="ww-custom-label">Length feet</span>
            <input class="ww-custom-input" data-field="lengthFeet" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-min="0" value="${escapeAttr(state.lengthFeet)}" placeholder="0">
          </label>
          <label>
            <span class="ww-custom-label">Length inches</span>
            <input class="ww-custom-input" data-field="lengthInches" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-min="0" data-max="11" value="${escapeAttr(state.lengthInches)}" placeholder="0">
          </label>
          `}
        </div>
      ` : `
        <label>
          <span class="ww-custom-label">Length feet</span>
          <input class="ww-custom-input" data-field="runnerPadLength" type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" data-min="${padCfg.minLengthFt}" data-max="${padCfg.maxLengthFt}" value="${escapeAttr(state.runnerPadLength)}" placeholder="0">
        </label>
      `
    ].join('');
  }

  function getStatePrice(type, config, state) {
    if (type === 'runner_pad') {
      const cfg = runnerPadConfig(config);
      const width = parseInt(state.runnerPadWidth, 10) || cfg.widthsFt[0];
      const length = parseInt(state.runnerPadLength, 10) || 0;
      const errors = [];
      if (!cfg.widthsFt.includes(width)) errors.push('Choose a valid width.');
      if (!length) errors.push('Add a length to see your price.');
      if (length && length < cfg.minLengthFt) errors.push('Length must be at least ' + cfg.minLengthFt + ' ft.');
      if (length > cfg.maxLengthFt) errors.push('Length must be ' + cfg.maxLengthFt + ' ft or less.');
      const floor = cfg.floorPriceByWidth[width];
      const rate = cfg.perFtRateByWidth[width];
      const total = length <= cfg.floorThresholdFt ? floor : Math.round(length * rate * 100) / 100;
      return { valid: errors.length === 0, errors, total, detail: length <= cfg.floorThresholdFt ? 'Minimum price' : length + ' ft x ' + formatMoney(rate) + '/ft' };
    }

    const cfg = type === 'rug_pad'
      ? {
          ...DEFAULT_RUG,
          pricePerSqFt: toNumber((config.metafields || {}).priceOverrideSqFt, toNumber((config.metafields || {}).pricePerSqFt, DEFAULT_RUG_PAD.pricePerSqFt)),
          maxWidthInches: toInt((config.metafields || {}).maxWidthInches, DEFAULT_RUG_PAD.maxWidthInches),
          maxLengthFeet: toInt((config.metafields || {}).maxLengthFeet, DEFAULT_RUG_PAD.maxLengthFeet),
          minOrderSqFt: 0
        }
      : customRugConfig(config);
    const dims = dimensionsFromState(state);
    if (type === 'runner') {
      const widthInches = parseWidthInches(state.runnerWidth);
      dims.widthFeet = Math.floor(widthInches / 12);
      dims.widthInches = widthInches % 12;
    }
    const errors = validateRug(cfg, dims, state.shape);
    if (type === 'rug_pad') {
      const totalWidth = dims.widthFeet * 12 + dims.widthInches;
      const totalLength = dims.lengthFeet * 12 + dims.lengthInches;
      if (totalWidth > 0 && totalWidth < DEFAULT_RUG_PAD.minWidthInches) errors.push('Width must be at least 2 ft.');
      if (totalLength > 0 && totalLength < DEFAULT_RUG_PAD.minLengthInches) errors.push('Length must be at least 3 ft.');
    }
    const breakdown = rugBreakdown(cfg, dims, state.shape);
    return {
      valid: errors.length === 0,
      errors,
      total: breakdown.total,
      areaSqFt: breakdown.areaSqFt,
      pricePerSqFt: breakdown.pricePerSqFt,
      detail: breakdown.areaSqFt > 0 ? formatArea(breakdown.areaSqFt) + ' at ' + formatMoney(breakdown.pricePerSqFt) + '/sq ft' : ''
    };
  }

  function render(root, config, state) {
    const result = getStatePrice(config.productType, config, state);
    root.innerHTML = `
      <div class="ww-custom-card">
        ${buildFields(config.productType, config, state)}
        <div class="ww-custom-price">
          <div>
            <div class="ww-custom-muted">Price</div>
            <strong data-ww-price-amount>${result.valid ? formatMoney(result.total) : '--'}</strong>
          </div>
          <div class="ww-custom-muted" data-ww-price-detail>${result.detail || ''}</div>
        </div>
        ${result.errors.length ? `<div class="ww-custom-error">${result.errors[0]}</div>` : ''}
        <div class="ww-custom-actions">
          <button class="ww-custom-atc button button--primary button--fixed" type="button" ${result.valid ? '' : 'disabled'}>
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">Add to cart</span>
          </button>
          <div class="ww-custom-muted">Custom products are cut to your selected dimensions.</div>
        </div>
      </div>
    `;
    bind(root, config, state);
  }

  function updatePreview(root, config, state) {
    const result = getStatePrice(config.productType, config, state);
    const amount = root.querySelector('[data-ww-price-amount]');
    const detail = root.querySelector('[data-ww-price-detail]');
    const atc = root.querySelector('.ww-custom-atc');
    const card = root.querySelector('.ww-custom-card');
    let error = root.querySelector('.ww-custom-error');

    if (amount) amount.textContent = result.valid ? formatMoney(result.total) : '--';
    if (detail) detail.textContent = result.detail || '';
    if (atc) atc.disabled = !result.valid;

    if (!card) return;
    if (result.errors.length) {
      if (!error) {
        error = document.createElement('div');
        error.className = 'ww-custom-error';
        const actions = card.querySelector('.ww-custom-actions');
        card.insertBefore(error, actions || null);
      }
      error.textContent = result.errors[0];
    } else if (error) {
      error.remove();
    }
  }

  function bind(root, config, state) {
    root.querySelectorAll('[data-field]').forEach((input) => {
      input.addEventListener('input', () => {
        const field = input.getAttribute('data-field');
        if (input.tagName === 'INPUT') {
          const cleaned = normalizeNumberInput(input, true);
          if (input.value !== cleaned) input.value = cleaned;
          state[field] = cleaned;
        } else {
          state[field] = input.value;
        }
        updatePreview(root, config, state);
      });
      input.addEventListener('change', () => {
        const field = input.getAttribute('data-field');
        if (input.tagName === 'INPUT') {
          const normalized = normalizeNumberInput(input, false);
          input.value = normalized;
          state[field] = normalized;
        } else {
          state[field] = input.value;
        }
        updatePreview(root, config, state);
      });
    });
    root.querySelectorAll('[data-shape]').forEach((button) => {
      button.addEventListener('click', () => {
        state.shape = button.getAttribute('data-shape');
        render(root, config, state);
      });
    });
    const atc = root.querySelector('.ww-custom-atc');
    if (atc) {
      atc.addEventListener('click', () => submit(root, config, state, atc));
    }
  }

  function pricePath(type) {
    if (type === 'runner_pad') return '/api/custom-runner-pads/price';
    if (type === 'rug_pad') return '/api/rug-pads/price';
    return '/api/custom-rugs/price';
  }

  async function postJson(config, path, body) {
    const base = String(config.apiBase || '').replace(/\/$/, '');
    const init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    };
    let res;
    try {
      res = await fetch(base + path, init);
    } catch (err) {
      if (base.includes('custom-api.wellwoven.com')) {
        res = await fetch(API_FALLBACK + path, init);
      } else {
        throw err;
      }
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  }

  function selectedVariantId(config, state) {
    if (config.productType === 'runner') {
      return numericVariantId(variantForWidth(config, state.runnerWidth));
    }
    return numericVariantId(state.selectedVariantId || config.selectedVariantId || firstAvailableVariant(config));
  }

  function priceRequestBody(config, state) {
    if (config.productType === 'runner_pad') {
      return {
        productHandle: config.productHandle,
        widthFt: parseInt(state.runnerPadWidth, 10),
        lengthFt: parseInt(state.runnerPadLength, 10)
      };
    }
    const dims = dimensionsFromState(state);
    if (config.productType === 'runner') {
      const widthInches = parseWidthInches(state.runnerWidth);
      dims.widthFeet = Math.floor(widthInches / 12);
      dims.widthInches = widthInches % 12;
    }
    if (config.productType === 'runner_pad') {
      dims.widthFeet = parseInt(state.runnerPadWidth, 10) || 0;
      dims.widthInches = 0;
      dims.lengthFeet = parseInt(state.runnerPadLength, 10) || 0;
      dims.lengthInches = 0;
    }
    return {
      productHandle: config.productHandle,
      shape: config.productType === 'rug_pad' ? undefined : state.shape,
      widthFeet: dims.widthFeet,
      widthInches: dims.widthInches,
      lengthFeet: dims.lengthFeet,
      lengthInches: dims.lengthInches
    };
  }

  function buildProperties(config, state, quote) {
    const result = getStatePrice(config.productType, config, state);
    const dims = dimensionsFromState(state);
    if (config.productType === 'runner') {
      const widthInches = parseWidthInches(state.runnerWidth);
      dims.widthFeet = Math.floor(widthInches / 12);
      dims.widthInches = widthInches % 12;
    }
    const shape = config.productType === 'runner_pad'
      ? 'runner_pad'
      : config.productType === 'rug_pad'
        ? 'rectangle'
        : state.shape;
    const displayShape = config.productType === 'runner'
      ? 'Rectangle'
      : config.productType === 'runner_pad'
        ? 'Runner Pad'
        : config.productType === 'rug_pad'
          ? 'Rug Pad'
          : state.shape === 'round' ? 'Round' : 'Rectangle';
    const widthLabel = config.productType === 'runner_pad'
      ? state.runnerPadWidth + "'"
      : formatDimension(dims.widthFeet, dims.widthInches);
    const lengthLabel = config.productType === 'runner_pad'
      ? state.runnerPadLength + "'"
      : state.shape === 'round'
        ? widthLabel + ' diameter'
        : formatDimension(dims.lengthFeet, dims.lengthInches);
    const props = {
      Shape: displayShape,
      Width: widthLabel,
      Length: lengthLabel,
      Price: '[ +' + formatMoney(parseFloat(quote.total)) + ' ]',
      _custom_price: quote.total,
      _price_signature: quote.signature,
      _product_id: quote.productId || config.productGid,
      _shape: shape,
      _widthFeet: String(dims.widthFeet),
      _widthInches: String(dims.widthInches),
      _lengthFeet: String(dims.lengthFeet),
      _lengthInches: String(dims.lengthInches)
    };
    if (result.areaSqFt) {
      props['Total Area'] = formatArea(result.areaSqFt);
      props['Price Per Sq Ft'] = formatMoney(result.pricePerSqFt);
    }
    if (config.productType === 'runner_pad') props.Thickness = runnerPadConfig(config).thicknessInches;
    return props;
  }

  async function addItemsToCart(items, activeElement) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    const body = {
      items,
      sections,
      sections_url: window.location.pathname
    };
    const res = await fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    const parsed = await res.json();
    if (!res.ok || parsed.status) throw new Error(parsed.description || 'Unable to add to cart.');
    const cartUrl = (window.theme && theme.routes && theme.routes.cart_url) || '/cart.js';
    const cartJson = await (await fetch(cartUrl, { headers: { 'Accept': 'application/json' } })).json();
    cartJson.sections = parsed.sections || {};
    if (window.theme && theme.pubsub) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'ww-custom-configurator',
        productVariantId: items[0].id,
        cart: cartJson
      });
    }
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));
    document.querySelector('cart-drawer')?.show(activeElement);
    patchCartDisplay(cartJson);
  }

  async function submit(root, config, state, button) {
    const current = getStatePrice(config.productType, config, state);
    if (!current.valid || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const error = root.querySelector('.ww-custom-error');
    if (error) error.remove();
    try {
      const quote = await postJson(config, pricePath(config.productType), priceRequestBody(config, state));
      await addItemsToCart([
        {
          id: selectedVariantId(config, state),
          quantity: 1,
          properties: buildProperties(config, state, quote)
        }
      ], button);
    } catch (err) {
      const card = root.querySelector('.ww-custom-card');
      if (card) {
        const errorEl = document.createElement('div');
        errorEl.className = 'ww-custom-error';
        errorEl.textContent = err.message || 'Unable to add to cart. Please try again.';
        card.appendChild(errorEl);
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function propValue(item, key) {
    const props = item && item.properties;
    if (!props) return null;
    if (Array.isArray(props)) {
      const found = props.find((prop) => prop && (prop.name === key || prop.key === key));
      return found ? found.value : null;
    }
    return props[key];
  }

  function hasCustomCartLine(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.some((item) => propValue(item, '_custom_price')));
  }

  function setAdditionalCheckoutButtonsHidden(hidden) {
    document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
      if (hidden) {
        el.hidden = true;
        el.dataset.wwCustomHidden = 'true';
        return;
      }
      if (el.dataset.wwCustomHidden === 'true') {
        el.hidden = false;
        delete el.dataset.wwCustomHidden;
      }
    });
  }

  function patchCartDisplay(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    let correctedSubtotal = 0;
    cart.items.forEach((item) => {
      const custom = parseFloat(propValue(item, '_custom_price'));
      const lineCents = Number.isFinite(custom)
        ? Math.round(custom * 100) * (item.quantity || 1)
        : item.final_line_price;
      correctedSubtotal += lineCents || 0;
      if (!Number.isFinite(custom)) return;
      const id = window.CSS && CSS.escape ? CSS.escape(item.key) : String(item.key).replace(/"/g, '\\"');
      document.querySelectorAll('#CartDrawer-Item-' + id + ', #CartItem-' + id).forEach((row) => {
        const price = row.querySelector('.price');
        if (price) price.textContent = moneyFromCents(lineCents);
      });
    });
    if (!hasCustomCartLine(cart)) {
      setAdditionalCheckoutButtonsHidden(false);
      return;
    }
    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      el.textContent = moneyFromCents(correctedSubtotal);
    });
    setAdditionalCheckoutButtonsHidden(true);
  }

  async function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((res) => res.json());
  }

  function collectDiscountCodes() {
    return unique(Array.from(document.querySelectorAll('[data-discount]')).map((el) => el.getAttribute('data-discount') || ''));
  }

  async function handleCheckout(event) {
    if (bypassCheckoutInterceptor) return;
    const submitter = event.submitter || document.activeElement;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    event.preventDefault();
    event.stopPropagation();
    submitter.setAttribute('aria-busy', 'true');
    const cart = await fetchCart().catch(() => null);
    if (!hasCustomCartLine(cart)) {
      submitter.removeAttribute('aria-busy');
      bypassCheckoutInterceptor = true;
      event.target.submit();
      return;
    }
    try {
      const apiBase = (window.WWCustomConfigurator && window.WWCustomConfigurator.apiBase) || window.WWCustomConfiguratorApiBase || 'https://custom-api.wellwoven.com';
      const base = apiBase.replace(/\/$/, '');
      const init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ cart, discountCodes: collectDiscountCodes() })
      };
      let res;
      try {
        res = await fetch(base + '/api/theme-cart/create-draft-order', init);
      } catch (fetchErr) {
        if (base.includes('custom-api.wellwoven.com')) {
          res = await fetch(API_FALLBACK + '/api/theme-cart/create-draft-order', init);
        } else {
          throw fetchErr;
        }
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.invoiceUrl) throw new Error(data.error || 'Unable to prepare checkout.');
      window.location.href = data.invoiceUrl;
    } catch (err) {
      window.alert(err.message || 'Unable to prepare checkout. Please try again.');
      submitter.removeAttribute('aria-busy');
    }
  }

  function initialState(config) {
    const first = firstAvailableVariant(config);
    const widths = runnerWidthOptions(config).values;
    const padCfg = runnerPadConfig(config);
    return {
      shape: 'rectangle',
      selectedVariantId: numericVariantId(config.selectedVariantId || first),
      widthFeet: '',
      widthInches: '',
      lengthFeet: '',
      lengthInches: '',
      runnerWidth: widths[0] || '',
      runnerPadWidth: padCfg.widthsFt[0],
      runnerPadLength: ''
    };
  }

  function init(root) {
    const config = readConfig(root);
    if (!config) return;
    window.WWCustomConfiguratorApiBase = config.apiBase || 'https://custom-api.wellwoven.com';
    window.WWCustomConfigurator = {
      apiBase: config.apiBase || 'https://custom-api.wellwoven.com',
      productHandle: config.productHandle,
      productType: config.productType,
      selectedVariantId: config.selectedVariantId,
      templateSuffix: config.templateSuffix
    };
    const state = initialState(config);
    if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS.variantChange) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event || !event.data || String(event.data.productId) !== String(config.product.id)) return;
        state.selectedVariantId = numericVariantId(event.data.variant);
        window.WWCustomConfigurator.selectedVariantId = state.selectedVariantId;
      });
    }
    render(root, config, state);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-ww-custom-configurator]').forEach(init);
    fetchCart().then(patchCartDisplay).catch(() => {});
  });
  document.addEventListener('cart:updated', (event) => patchCartDisplay(event.detail && event.detail.cart));
  document.addEventListener('submit', handleCheckout, true);
})();
